import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatShortDate } from './metrics';
import { MetricSnapshot } from './types';

const CHART_WIDTH = 560;
const CHART_HEIGHT = 218;
const PADDING = {
  top: 18,
  right: 18,
  bottom: 44,
  left: 88,
};

const plotWidth = CHART_WIDTH - PADDING.left - PADDING.right;
const plotHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;

const clampRange = (min: number, max: number) => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { min: 0, max: 1 };
  }
  if (min === max) {
    const fallback = Math.max(Math.abs(max) * 0.1, 1);
    return { min: min - fallback, max: max + fallback };
  }
  const padding = (max - min) * 0.12;
  return { min: min - padding, max: max + padding };
};

const getPoint = (value: number, index: number, count: number, min: number, max: number) => {
  const x = PADDING.left + (count > 1 ? (plotWidth / (count - 1)) * index : plotWidth / 2);
  const normalized = (value - min) / (max - min);
  const y = PADDING.top + plotHeight - normalized * plotHeight;
  return { x, y };
};

const buildPolylinePoints = (values: number[], min: number, max: number) =>
  values
    .map((value, index) => {
      const point = getPoint(value, index, values.length, min, max);
      return `${point.x.toFixed(1)},${point.y.toFixed(1)}`;
    })
    .join(' ');

const getTicks = (min: number, max: number) => [max, min + (max - min) / 2, min];

interface LineComparisonChartProps {
  orgSeries: MetricSnapshot[];
  selectedSeries: MetricSnapshot[];
  metric: (snapshot: MetricSnapshot) => number;
  selectedColor?: string;
  orgColor?: string;
  selectedLabel?: string;
  orgLabel?: string;
  valueFormatter?: (value: number) => string;
}

export const LineComparisonChart: React.FC<LineComparisonChartProps> = ({
  orgSeries,
  selectedSeries,
  metric,
  selectedColor = '#0f766e',
  orgColor = '#3e473a',
  selectedLabel = 'Selected',
  orgLabel = 'Org',
  valueFormatter = (value) => String(value),
}) => {
  const orgValues = orgSeries.map(metric);
  const selectedValues = selectedSeries.map(metric);
  const allValues = [...orgValues, ...selectedValues];
  const { min, max } = clampRange(Math.min(...allValues), Math.max(...allValues));
  const orgPoints = buildPolylinePoints(orgValues, min, max);
  const selectedPoints = buildPolylinePoints(selectedValues, min, max);
  const latestSelected = selectedValues[selectedValues.length - 1] ?? 0;
  const latestOrg = orgValues[orgValues.length - 1] ?? 0;
  const ticks = getTicks(min, max);

  return (
    <Box
      sx={{
        width: '100%',
        border: '1px solid',
        borderColor: alpha('#1f2a20', 0.1),
        borderRadius: 1,
        bgcolor: '#ffffff',
        overflow: 'hidden',
      }}
    >
      <svg
        width="100%"
        height="238"
        viewBox={`0 0 ${CHART_WIDTH} 238`}
        role="img"
        aria-label={`${selectedLabel} versus ${orgLabel} trend`}
      >
        <rect x="0" y="0" width={CHART_WIDTH} height="238" fill="#fff" />

        {ticks.map((tick) => {
          const y = getPoint(tick, 0, 1, min, max).y;
          return (
            <g key={tick}>
              <line
                x1={PADDING.left}
                x2={CHART_WIDTH - PADDING.right}
                y1={y}
                y2={y}
                stroke={alpha('#1f2a20', 0.12)}
                strokeWidth="1"
              />
              <text x={PADDING.left - 8} y={y + 3} textAnchor="end" fill={alpha('#1f2a20', 0.64)} fontSize="10" fontWeight="700">
                {valueFormatter(tick)}
              </text>
            </g>
          );
        })}

        <line
          x1={PADDING.left}
          x2={PADDING.left}
          y1={PADDING.top}
          y2={CHART_HEIGHT - PADDING.bottom}
          stroke={alpha('#1f2a20', 0.28)}
          strokeWidth="1"
        />
        <line
          x1={PADDING.left}
          x2={CHART_WIDTH - PADDING.right}
          y1={CHART_HEIGHT - PADDING.bottom}
          y2={CHART_HEIGHT - PADDING.bottom}
          stroke={alpha('#1f2a20', 0.28)}
          strokeWidth="1"
        />

        {selectedSeries.map((snapshot, index) => {
          const point = getPoint(metric(snapshot), index, selectedSeries.length, min, max);
          const showLabel = selectedSeries.length <= 8 || index === 0 || index === selectedSeries.length - 1;
          return (
            <g key={snapshot.date}>
              <line
                x1={point.x}
                x2={point.x}
                y1={CHART_HEIGHT - PADDING.bottom}
                y2={CHART_HEIGHT - PADDING.bottom + 5}
                stroke={alpha('#1f2a20', 0.3)}
              />
              {showLabel ? (
                <text
                  x={point.x}
                  y={CHART_HEIGHT - 18}
                  textAnchor="middle"
                  fill={alpha('#1f2a20', 0.68)}
                  fontSize="10"
                  fontWeight="700"
                >
                  {formatShortDate(snapshot.date)}
                </text>
              ) : null}
            </g>
          );
        })}

        <polyline
          points={orgPoints}
          fill="none"
          stroke={orgColor}
          strokeWidth="2"
          strokeDasharray="6 5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <polyline
          points={selectedPoints}
          fill="none"
          stroke={selectedColor}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {selectedValues.map((value, index) => {
          const point = getPoint(value, index, selectedValues.length, min, max);
          return <circle key={`${value}-${index}`} cx={point.x} cy={point.y} r="3.4" fill={selectedColor} stroke="#fff" strokeWidth="1.5" />;
        })}
      </svg>

      <Stack
        direction="row"
        spacing={1.5}
        sx={{ px: 1.5, pb: 1.15, pt: 0, justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 18, height: 3, bgcolor: selectedColor, borderRadius: 1 }} />
            <Typography variant="caption" sx={{ color: '#273127', fontWeight: 800 }}>
              {selectedLabel}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 18, height: 3, borderTop: `2px dashed ${orgColor}` }} />
            <Typography variant="caption" sx={{ color: alpha('#273127', 0.72), fontWeight: 800 }}>
              {orgLabel}
            </Typography>
          </Stack>
        </Stack>
        <Typography variant="caption" sx={{ color: alpha('#273127', 0.72), fontWeight: 800 }}>
          {valueFormatter(latestSelected)} / {valueFormatter(latestOrg)}
        </Typography>
      </Stack>
    </Box>
  );
};

interface DepotBarProps {
  label: string;
  detail: string;
  value: number;
  max: number;
  color: string;
  valueLabel: string;
}

export const DepotBar: React.FC<DepotBarProps> = ({ label, detail, value, max, color, valueLabel }) => {
  const width = max > 0 ? Math.max(8, Math.min(100, (value / max) * 100)) : 0;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 800, color: '#1f2a20', lineHeight: 1.1 }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: alpha('#1f2a20', 0.62), lineHeight: 1.1 }}>
            {detail}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 800, color: '#1f2a20', flexShrink: 0 }}>
          {valueLabel}
        </Typography>
      </Stack>
      <Box
        sx={{
          mt: 0.85,
          height: 8,
          bgcolor: alpha('#1f2a20', 0.09),
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ height: '100%', width: `${width}%`, bgcolor: color, borderRadius: 1 }} />
      </Box>
    </Box>
  );
};
